<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';

    import uniqueId from 'lodash/uniqueId';
    import { afterUpdate } from 'svelte';
    import DeviceDetector from 'svelte-device-detector';
    import clickOutside from 'svelte-outside-click';
    import { animateScroll } from 'svelte-scrollto-element';

    import DropdownInputActionButtons from './DropdownInputActionButtons.svelte';
    import DropdownInputContent from './DropdownInputContent.svelte';
    import BottomSheet from '../../../_wrappers/BottomSheet.svelte';
    import Dropdown from '../../../_wrappers/Dropdown.svelte';
    import Emoji from '../../../_wrappers/Emoji.svelte';
    import StatusIcon from '../StatusIcon.svelte';

    import type { Language } from '$lib/types/Language';

    export let placeholder = '';
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let type = 'text';
    export let finalFormInput: {
        name: string;
        value: string;
        onFocus: () => void;
        onBlur: () => void;
        onChange: (value: string) => void;
    } = {
        name: '',
        value: '',
        onFocus: () => {},
        onBlur: () => {},
        onChange: () => {},
    };

    export let customOptions: { multiSelect: boolean; twoColumn: boolean; choices: string[] } = {
        multiSelect: false,
        twoColumn: false,
        choices: [],
    };
    export let errorMessage = '';
    export let focusColor = '';
    export let outlineColor = '';

    export let language: Language = 'en';
    // Property introduced for Storybook to control the initial open state of the dropdown
    export let initialOpen = false;

    const dropdownId = uniqueId('dropdown');

    let inputClasses: string;
    let inputWrapperClasses: string;
    let chevronSvgClasses: string;

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let showCheckmark = ['email', 'phone', 'url'].includes(type);
    let selectedChoices: string[] = [];
    let visualInputValue: string | undefined;
    let open = initialOpen;
    let overlayButtonElement: HTMLElement | undefined;

    const { twoColumn, multiSelect, choices } = customOptions;

    afterUpdate(() => {
        showError = invalid && touched;
        showSuccess = !invalid && touched && !pristine;
        showCheckmark = ['email', 'phone', 'url'].includes(type);
    });

    const handleInputClick = () => {
        if (open) {
            hideDropdown();
        } else {
            showDropdown();
        }
    };

    const showDropdown = () => {
        scrollIntoView();
        finalFormInput.onFocus();
        open = true;
    };

    const hideDropdown = () => {
        open = false;
        finalFormInput.onBlur();
    };

    const handleClickOutside = () => {
        if (!open) return;

        hideDropdown();
    };

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
        const travelBack = event.shiftKey && event.key === 'Tab';

        if (travelBack && open) {
            hideDropdown();
        }

        // Handle space or enter click
        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();

            if (open) {
                hideDropdown();
            } else {
                showDropdown();
            }
        }

        // Handle arrow keys when dropdown is open - move focus to first button
        if (open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            const firstButton = document.querySelector(
                `#${dropdownId} button`,
            ) as HTMLButtonElement;
            firstButton?.focus();
        }

        // Handle Escape to close dropdown
        if (event.key === 'Escape' && open) {
            event.preventDefault();
            hideDropdown();
            overlayButtonElement?.focus();
        }
    };

    const handleSave = () => {
        finalFormInput.onChange(selectedChoices.sort().join('; '));
        visualInputValue = selectedChoices.sort().join(', ');
        hideDropdown();
    };

    const handleClear = () => {
        selectedChoices = [];
        visualInputValue = undefined;
        finalFormInput.onChange('');
    };

    const handleSelect = (event: MouseEvent, optionValue: string) => {
        event.preventDefault();

        if (multiSelect) {
            if (selectedChoices.includes(optionValue)) {
                selectedChoices = selectedChoices.filter((choice) => choice !== optionValue);
            } else {
                selectedChoices = [...selectedChoices, optionValue];
            }
        } else {
            finalFormInput.onChange(optionValue);
            hideDropdown();
        }
    };

    const scrollIntoView = () => {
        const dropdown = document.querySelector(`#${dropdownId}`) as HTMLElement;

        if (dropdown) {
            // Offset the height of the input field
            animateScroll?.scrollTo({ element: dropdown, offset: -64 });
        }
    };

    const borderRadius = getBorderRadius('fieldsWrapper');

    $: inputWrapperClasses = cn(
        'fun-relative fun-box-border fun-flex fun-overflow-hidden fun-transition-all',
        borderRadius,
        {
            ['fun-h-20']: showError,
        },
    );

    $: inputClasses = cn(
        'fun-box-border fun-w-full fun-appearance-none fun-border fun-bg-white fun-pl-4 fun-pr-12 fun-align-middle fun-font-ui fun-text-base fun-font-light fun-text-black fun-placeholder-gray-500 fun-outline-none fun-ring-2 fun-ring-transparent fun-transition-all focus:fun-border-[var(--focusColor)] focus:fun-ring-[var(--outlineColor)]',
        borderRadius,
        {
            ['fun-pl-12']: showEmoji,
            ['fun-h-20 fun-pb-[26px]']: showError,
            ['fun-h-14']: !showError,
            ['fun-pr-24']: !required && pristine,
        },
    );

    $: chevronSvgClasses = cn(
        'fun-pointer-events-none fun-absolute fun-top-5 fun-size-4 fun-transform fun-cursor-pointer fun-text-gray-500 fun-transition-all fun-duration-[400ms] fun-ease-[cubic-bezier(0.16,1,0.3,1)]',
        {
            'fun--rotate-180': !open,
            'fun-rotate-0': open,
            'fun-right-16': showError,
            'fun-right-6': !showError,
        },
    );
</script>

<div class="fun-relative" use:clickOutside={handleClickOutside}>
    <div class={inputWrapperClasses}>
        {#if showEmoji}
            <u
                class="fun-absolute fun-left-4 fun-flex fun-h-14 fun-select-none fun-items-center fun-no-underline"
                aria-hidden="true"
            >
                <Emoji {emoji} size="20px" />
            </u>
        {/if}

        {#if !required && !invalid && pristine}
            <div
                class="fun-absolute fun-right-14 fun-flex fun-h-full fun-items-center fun-text-xs fun-font-light fun-text-gray-500 fun-no-underline"
            >
                <span>(optional)</span>
            </div>
        {/if}

        <StatusIcon {showCheckmark} {showError} {showSuccess} />

        <div
            class={cn('fun-absolute fun-inset-0 fun-z-10 fun-cursor-pointer', borderRadius)}
            on:click={handleInputClick}
            on:keydown={handleGlobalKeyDown}
            role="button"
            tabindex="0"
            aria-label={placeholder}
            bind:this={overlayButtonElement}
        />

        <input
            readonly
            class={inputClasses}
            type="text"
            {placeholder}
            name={finalFormInput.name}
            value={visualInputValue || finalFormInput.value}
            on:blur={finalFormInput.onBlur}
            on:input={() => finalFormInput.onChange(finalFormInput.value)}
            style="--outlineColor: {outlineColor}; --focusColor: {focusColor}"
        />

        <svg
            class={chevronSvgClasses}
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6.55806 0.433058C6.80214 0.188981 7.19786 0.188981 7.44194 0.433058L13.6919 6.68306C13.936 6.92714 13.936 7.32286 13.6919 7.56694C13.4479 7.81102 13.0521 7.81102 12.8081 7.56694L7 1.75888L1.19194 7.56694C0.947864 7.81102 0.552136 7.81102 0.308058 7.56694C0.0639806 7.32286 0.0639806 6.92714 0.308058 6.68306L6.55806 0.433058Z"
                fill="#6B7280"
            />
        </svg>

        {#if showError}
            <div
                class={cn(
                    'fun-absolute fun-left-4 fun-top-11 fun-text-xs fun-font-normal fun-leading-tight fun-text-red-600',
                    {
                        'fun-left-14': showEmoji,
                    },
                )}
            >
                {errorMessage}
            </div>
        {/if}
    </div>

    <DeviceDetector showInDevice={['mobile', 'tablet']}>
        <BottomSheet
            {open}
            title={placeholder}
            handleClose={hideDropdown}
            actionButtonsComponent={multiSelect ? DropdownInputActionButtons : undefined}
            actionButtonsProps={{
                disabled: !selectedChoices?.length,
                language,
                twoColumn,
                handleSave,
                handleClear,
                open,
            }}
            {language}
        >
            <DropdownInputContent
                {choices}
                {selectedChoices}
                value={finalFormInput.value}
                {twoColumn}
                {handleSelect}
                {open}
                inputElement={overlayButtonElement}
            />
        </BottomSheet>
    </DeviceDetector>

    <DeviceDetector showInDevice="desktop">
        <Dropdown id={dropdownId} {open} contentClass="fun-w-full">
            <DropdownInputContent
                {choices}
                {selectedChoices}
                value={finalFormInput.value}
                {twoColumn}
                {handleSelect}
                {open}
                inputElement={overlayButtonElement}
            />
            {#if multiSelect}
                <DropdownInputActionButtons
                    disabled={!selectedChoices?.length}
                    {language}
                    {twoColumn}
                    {handleSave}
                    {handleClear}
                    {open}
                />
            {/if}
        </Dropdown>
    </DeviceDetector>
</div>
